import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext.tsx';
import { subscriptionAPI } from '../services/api.ts';
import { CheckIcon, UserIcon, ClipboardDocumentListIcon } from '@heroicons/react/24/outline';
import { useSearchParams } from 'react-router-dom';

interface SubscriptionPlan {
  id: string;
  name: string;
  description: string;
  price_monthly: number;
  price_yearly: number;
  hire_limit: number | null; // null = unlimited
  user_limit: number | null; // null = unlimited
  features: string[];
  stripe_price_id_monthly?: string;
  stripe_price_id_yearly?: string;
  popular?: boolean;
  current?: boolean;
}

const Subscription: React.FC = () => {
  const { customer, refreshProfile } = useAuth();
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [searchParams] = useSearchParams();

  const fetchPlans = useCallback(async () => {
    try {
      const response = await subscriptionAPI.getPlans();
      const fetchedPlans = response.data.plans.map((plan: any) => ({
        ...plan,
        current: plan.id === customer?.subscription_plan,
        popular: plan.name === 'Teams Edition'
      }));
      setPlans(fetchedPlans);
    } catch (error) {
      console.error('Error fetching subscription plans:', error);
      // Fallback to mock data if API fails
      const mockPlans: SubscriptionPlan[] = [
        {
          id: 'free',
          name: 'Free Edition',
          description: 'Perfect for getting started',
          price_monthly: 0,
          price_yearly: 0,
          hire_limit: 5,
          user_limit: 2,
          features: [
            'Up to 5 equipment hires',
            'Up to 2 team members',
            'Basic equipment tracking',
            'Email support',
            'Mobile app access'
          ],
          current: customer?.subscription_plan === 'free'
        },
        {
          id: 'teams',
          name: 'Teams Edition',
          description: 'Ideal for growing teams',
          price_monthly: 79,
          price_yearly: 790,
          hire_limit: 25,
          user_limit: 5,
          features: [
            'Up to 25 equipment hires',
            'Up to 5 team members',
            'Advanced equipment tracking',
            'Priority email support',
            'Mobile app access',
            'Basic reporting',
            'Supplier management'
          ],
          popular: true,
          current: customer?.subscription_plan === 'teams'
        },
        {
          id: 'business',
          name: 'Business Edition',
          description: 'For established businesses',
          price_monthly: 129,
          price_yearly: 1290,
          hire_limit: 50,
          user_limit: 10,
          features: [
            'Up to 50 equipment hires',
            'Up to 10 team members',
            'Advanced equipment tracking',
            'Priority phone & email support',
            'Mobile app access',
            'Advanced reporting & analytics',
            'Full supplier management',
            'Site management',
            'Custom integrations'
          ],
          current: customer?.subscription_plan === 'business'
        },
        {
          id: 'unlimited',
          name: 'Unlimited',
          description: 'For large enterprises',
          price_monthly: 179,
          price_yearly: 1790,
          hire_limit: null,
          user_limit: null,
          features: [
            'Unlimited equipment hires',
            'Unlimited team members',
            'Advanced equipment tracking',
            'Dedicated account manager',
            'Mobile app access',
            'Advanced reporting & analytics',
            'Full supplier management',
            'Site management',
            'Custom integrations',
            'API access',
            'White-label options'
          ],
          current: customer?.subscription_plan === 'unlimited'
        }
      ];
      setPlans(mockPlans);
    }
    setLoading(false);
  }, [customer?.subscription_plan]);

  const handleStripeRedirect = useCallback(async () => {
    const sessionId = searchParams.get('session_id');
    const success = searchParams.get('success');
    const canceled = searchParams.get('canceled');

    if (success === 'true' && sessionId) {
      try {
        console.log('Processing successful Stripe payment...');
        await subscriptionAPI.handleSuccess(sessionId);
        await refreshProfile(); // Refresh user data
        setSuccessMessage('🎉 Subscription updated successfully! Welcome to your new plan.');
        // Refresh plans to update current plan status
        fetchPlans();
        
        // Clean up URL parameters
        window.history.replaceState({}, document.title, window.location.pathname);
      } catch (error) {
        console.error('Error processing subscription success:', error);
        setErrorMessage('Failed to update subscription. Please contact support.');
      }
    } else if (canceled === 'true') {
      setErrorMessage('Payment was canceled. You can try again anytime.');
      // Clean up URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [searchParams, refreshProfile, fetchPlans]);

  useEffect(() => {
    fetchPlans();
    handleStripeRedirect();
  }, [fetchPlans, handleStripeRedirect]);

  // Refresh plans when customer subscription changes
  useEffect(() => {
    if (customer?.subscription_plan) {
      fetchPlans();
    }
  }, [customer?.subscription_plan, fetchPlans]);

  const handlePlanAction = async (plan: SubscriptionPlan) => {
    const currentPlan = customer?.subscription_plan;
    const planHierarchy = ['free', 'teams', 'business', 'unlimited'];
    const currentIndex = planHierarchy.indexOf(currentPlan || 'free');
    const targetIndex = planHierarchy.indexOf(plan.id);

    // Handle downgrade to free
    if (plan.id === 'free' && currentPlan !== 'free') {
      handleDowngradeClick();
      return;
    }

    // Handle downgrade to paid plan (teams/business)
    if (targetIndex < currentIndex && plan.id !== 'free') {
      handleDowngradeToPaidPlan(plan);
      return;
    }

    // Handle upgrade to paid plan
    if (targetIndex > currentIndex) {
      setProcessing(plan.id);
      try {
        const priceId = plan.stripe_price_id_monthly;

        if (!priceId) {
          throw new Error('Price ID not available for this plan');
        }

        const response = await subscriptionAPI.createCheckout(priceId, 'month');
        
        // Redirect to Stripe Checkout
        window.location.href = response.data.checkoutUrl;
      } catch (error) {
        console.error('Error creating checkout session:', error);
        setErrorMessage('Failed to start checkout process. Please try again.');
      } finally {
        setProcessing(null);
      }
      return;
    }

    // Handle same plan (shouldn't happen due to button state)
    if (plan.current) {
      return;
    }
  };

  const handleDowngradeToFree = async () => {
    try {
      await subscriptionAPI.downgradeToFree();
      await refreshProfile();
      setSuccessMessage('✅ Subscription will be downgraded to Free Edition at your next renewal date. You\'ll keep your current plan until then.');
      // Refresh plans to update current plan status
      fetchPlans();
    } catch (error) {
      console.error('Error downgrading to free:', error);
      setErrorMessage('Failed to downgrade subscription. Please try again.');
    } finally {
      setShowConfirmModal(false);
    }
  };

  const handleDowngradeToPaidPlan = async (plan: SubscriptionPlan) => {
    setProcessing(plan.id);
    try {
      const priceId = plan.stripe_price_id_monthly;

      if (!priceId) {
        throw new Error('Price ID not available for this plan');
      }

      const response = await subscriptionAPI.createCheckout(priceId, 'month');
      
      // Redirect to Stripe Checkout
      window.location.href = response.data.checkoutUrl;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      setErrorMessage('Failed to start checkout process. Please try again.');
    } finally {
      setProcessing(null);
    }
  };

  const handleDowngradeClick = () => {
    setShowConfirmModal(true);
  };

  const clearMessages = () => {
    setSuccessMessage(null);
    setErrorMessage(null);
  };

  // Auto-clear messages after 5 seconds
  useEffect(() => {
    if (successMessage || errorMessage) {
      const timer = setTimeout(clearMessages, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, errorMessage]);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(price);
  };

  const formatLimit = (limit: number | null | undefined) => {
    if (limit === null || limit === undefined) return 'Unlimited';
    return limit.toString();
  };

  const getCurrentPrice = (plan: SubscriptionPlan) => {
    return plan.price_monthly; // Always use monthly pricing
  };

  const getButtonText = (plan: SubscriptionPlan) => {
    if (plan.current) return 'Current Plan';
    if (processing === plan.id) return 'Processing...';
    
    const currentPlan = customer?.subscription_plan;
    const planHierarchy = ['free', 'teams', 'business', 'unlimited'];
    const currentIndex = planHierarchy.indexOf(currentPlan || 'free');
    const targetIndex = planHierarchy.indexOf(plan.id);
    
    if (targetIndex > currentIndex) {
      return 'Upgrade';
    } else if (targetIndex < currentIndex) {
      return 'Downgrade';
    }
    
    return 'Select Plan';
  };

  const getButtonStyle = (plan: SubscriptionPlan) => {
    if (plan.current) return 'btn btn-primary';
    if (processing === plan.id) return 'bg-gray-400 text-white cursor-not-allowed';
    
    const currentPlan = customer?.subscription_plan;
    const planHierarchy = ['free', 'teams', 'business', 'unlimited'];
    const currentIndex = planHierarchy.indexOf(currentPlan || 'free');
    const targetIndex = planHierarchy.indexOf(plan.id);
    
    // Upgrade button (green)
    if (targetIndex > currentIndex) {
      return 'btn btn-success';
    }
    
    // Downgrade button (orange)
    if (targetIndex < currentIndex) {
      return 'btn btn-secondary';
    }
    
    // Default styling for popular plans
    if (plan.popular) return 'bg-primary-600 hover:bg-primary-700 text-white';
    return 'bg-white border-2 border-primary-600 text-primary-600 hover:bg-primary-50';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900">Choose Your Plan</h1>
        <p className="mt-4 text-lg text-gray-600">
          Select the perfect plan for your equipment management needs
        </p>

      </div>

      {/* Success/Error Messages */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-green-800">{successMessage}</p>
            </div>
            <div className="ml-auto pl-3">
              <button
                onClick={clearMessages}
                className="inline-flex text-green-400 hover:text-green-600"
              >
                <span className="sr-only">Dismiss</span>
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {errorMessage && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-red-800">{errorMessage}</p>
            </div>
            <div className="ml-auto pl-3">
              <button
                onClick={clearMessages}
                className="inline-flex text-red-400 hover:text-red-600"
              >
                <span className="sr-only">Dismiss</span>
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Billing Toggle - Commented out for monthly-only billing */}
      {/* 
      <div className="flex justify-center">
        <div className="bg-gray-100 p-1 rounded-lg">
          <button
            onClick={() => setBillingInterval('month')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              billingInterval === 'month'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setBillingInterval('year')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              billingInterval === 'year'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Annual <span className="text-green-600 text-xs">(Save 10%)</span>
          </button>
        </div>
      </div>
      */}

      {/* Plans Grid */}
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-3 xl:grid-cols-4">
        {plans.map((plan) => (
          <div
            key={plan.id}
            className={`relative bg-white dark:bg-gray-800 rounded-2xl shadow-lg border-2 p-8 ${
              plan.popular ? 'border-primary-500' : 'border-gray-200'
            } ${plan.current ? 'ring-2 ring-primary-500' : ''}`}
          >
            {/* Popular Badge */}
            {plan.popular && (
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-primary-600 text-white px-4 py-1 rounded-full text-sm font-medium">
                  Most Popular
                </span>
              </div>
            )}

            {/* Current Plan Badge */}
            {plan.current && (
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-green-600 text-white px-4 py-1 rounded-full text-sm font-medium">
                  Current Plan
                </span>
              </div>
            )}

            {/* Plan Header */}
            <div className="text-center mb-8">
              <h3 className="text-xl font-bold text-gray-900">{plan.name}</h3>
              <p className="text-gray-600 mt-2">{plan.description}</p>
              <div className="mt-4">
                <span className="text-4xl font-bold text-gray-900">
                  {formatPrice(getCurrentPrice(plan))}
                </span>
                {getCurrentPrice(plan) > 0 && (
                  <span className="text-gray-600">/month</span>
                )}
              </div>
            </div>

            {/* Limits */}
            <div className="mb-6 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <ClipboardDocumentListIcon className="h-5 w-5 text-gray-400 mr-2" />
                  <span className="text-sm text-gray-600">Equipment Hires</span>
                </div>
                <span className="font-semibold text-gray-900">
                  {formatLimit(plan.hire_limit)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <UserIcon className="h-5 w-5 text-gray-400 mr-2" />
                  <span className="text-sm text-gray-600">Team Members</span>
                </div>
                <span className="font-semibold text-gray-900">
                  {formatLimit(plan.user_limit)}
                </span>
              </div>
            </div>

            {/* Features */}
            <div className="mb-8">
              <ul className="space-y-3">
                {plan.features.map((feature, index) => (
                  <li key={index} className="flex items-start">
                    <CheckIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-gray-600">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* CTA Button */}
            <button
              onClick={() => handlePlanAction(plan)}
              disabled={plan.current || processing === plan.id}
              className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${getButtonStyle(plan)}`}
            >
              {getButtonText(plan)}
            </button>
          </div>
        ))}
      </div>

      {/* Additional Info */}
      <div className="text-center text-sm text-gray-500">
        <p>All plans include 30-day money-back guarantee</p>
        <p className="mt-1">All payments via card procesing using Stripe. Prices exclude VAT.</p>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-[28rem] shadow-lg rounded-md bg-white dark:bg-gray-800">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100">
                <svg className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mt-4">Schedule Downgrade to Free Edition?</h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  This will schedule your subscription to be downgraded to Free Edition at your next renewal date. 
                  You'll keep your current plan and features until then, with no refund needed.
                </p>
              </div>
              <div className="items-center px-4 py-3 flex justify-center gap-3">
                <button
                  onClick={handleDowngradeToFree}
                  className="btn btn-secondary"
                >
                  Yes
                </button>
                <button
                  onClick={() => setShowConfirmModal(false)}
                  className=" btn btn-soft"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Subscription;
